import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useLang } from "@/context/LanguageContext";

const Shop = () => {
  const { lang, t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const search = searchParams.get("search") || "";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("name");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.append("category", category);
    if (search) params.append("search", search);
    params.append("limit", "60");
    api.get(`/products?${params.toString()}`).then((r) => {
      let items = r.data.items || [];
      if (sort === "price_asc") items = [...items].sort((a, b) => (a.sale_price || a.price) - (b.sale_price || b.price));
      else if (sort === "price_desc") items = [...items].sort((a, b) => (b.sale_price || b.price) - (a.sale_price || a.price));
      else items = [...items].sort((a, b) => (a.name_el || "").localeCompare(b.name_el || ""));
      setProducts(items);
      setLoading(false);
    });
  }, [category, search, sort]);

  const setCategory = (slug) => {
    if (slug) searchParams.set("category", slug);
    else searchParams.delete("category");
    setSearchParams(searchParams);
  };

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <p className="overline mb-2">Boutique</p>
        <h1 className="font-serif text-4xl lg:text-5xl">{t("shop.title")}</h1>
        {search && (
          <p className="text-sm text-[#736B66] mt-2">«{search}» — {products.length} {t("shop.results")}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar filters */}
        <aside className="space-y-6" data-testid="filters-sidebar">
          <div>
            <p className="overline mb-4">{t("shop.filters")}</p>
            <div className="space-y-2">
              <button
                onClick={() => setCategory("")}
                className={`block text-left text-sm w-full py-1 ${!category ? "text-[#D4AF37] font-semibold" : "text-[#2C1E16] hover:text-[#D4AF37]"}`}
                data-testid="filter-all"
              >
                {t("shop.allCategories")}
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.slug)}
                  className={`block text-left text-sm w-full py-1 ${category === c.slug ? "text-[#D4AF37] font-semibold" : "text-[#2C1E16] hover:text-[#D4AF37]"}`}
                  data-testid={`filter-cat-${c.slug}`}
                >
                  {lang === "el" ? c.name_el : c.name_en}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-4">{t("shop.sortBy")}</p>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="luxury-input-box"
              data-testid="sort-select"
            >
              <option value="name">{t("shop.sortName")}</option>
              <option value="price_asc">{t("shop.sortPriceAsc")}</option>
              <option value="price_desc">{t("shop.sortPriceDesc")}</option>
            </select>
          </div>
        </aside>

        {/* Products grid */}
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-[#F5F2ED] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#736B66] mb-6">{t("shop.noResults")}</p>
              <Link to="/shop" className="luxury-btn-outline">Reset</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
